import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import { addDays, addMonths, addWeeks, format, parseISO } from 'date-fns';
import { Input } from 'src/ui/input';
import Banner from '@organisms/Banner';
import { Button } from '@components/ButtonV2';
import {
  AccessPeriodUnitKind,
  useValidateB2bCodeLazyQuery,
} from '@gql/generated';
import { CONTACT_EMAIL } from '@lib/constants';

export const B2bInfo = ({
  onCodeValidated,
  b2bCodeValue,
  b2bCodeChange,
  b2bCodeError,
}) => {
  const inputRef = useRef();

  const [validateCode, { data: codeValidation, loading }] =
    useValidateB2bCodeLazyQuery({
      onCompleted(data) {
        if (data.validateB2bCode.isCodeValid) {
          onCodeValidated(data.validateB2bCode.code);
        } else {
          onCodeValidated(null);
        }
      },
    });

  useEffect(() => {
    if (b2bCodeValue) {
      validateCode({ variables: { code: b2bCodeValue } });
    }
  }, []);

  let validation_message = null;
  if (b2bCodeValue && codeValidation?.validateB2bCode?.code === b2bCodeValue) {
    if (codeValidation?.validateB2bCode?.isCodeValid === false) {
      validation_message = (
        <p className="mt-1 mb-0 text-xs text-destructive font-medium">
          <Trans
            id="register_v2.form.one_time_code.invalid_code_message"
            components={[
              <a
                className="text-inherit underline"
                href={`mailto:${CONTACT_EMAIL}`}
              >
                {CONTACT_EMAIL}
              </a>,
            ]}
          />
        </p>
      );
    } else if (codeValidation?.validateB2bCode?.isCodeValid === true) {
      let date = null;
      if (codeValidation.validateB2bCode?.package) {
        date = new Date();
        if (codeValidation.validateB2bCode.package.playAccessUntil) {
          date = parseISO(
            codeValidation.validateB2bCode.package.playAccessUntil
          );
        } else if (
          codeValidation.validateB2bCode.package.playAccessPeriod.unit ===
          AccessPeriodUnitKind['Months']
        ) {
          date = addMonths(
            date,
            codeValidation.validateB2bCode.package.playAccessPeriod.value
          );
        } else if (
          codeValidation.validateB2bCode.package.playAccessPeriod.unit ===
          AccessPeriodUnitKind['Weeks']
        ) {
          date = addWeeks(
            date,
            codeValidation.validateB2bCode.package.playAccessPeriod.value
          );
        } else if (
          codeValidation.validateB2bCode.package.playAccessPeriod.unit ===
          AccessPeriodUnitKind['Days']
        ) {
          date = addDays(
            date,
            codeValidation.validateB2bCode.package.playAccessPeriod.value
          );
        }
      }

      validation_message = (
        <Banner appearance="success" className="!py-4 px-5 mt-1">
          <Trans
            id="register_v2.form.one_time_code.valid_code_message"
            values={{
              date: format(date, 'dd-MM-yyyy'),
              client: codeValidation.validateB2bCode.package.client.name,
            }}
          />
        </Banner>
      );
    }
  } else if (b2bCodeValue && b2bCodeError) {
    validation_message = (
      <p className="mt-1 mb-0 text-xs text-destructive font-medium">
        {b2bCodeError}
      </p>
    );
  }

  return (
    <div className="pb-6">
      <div className="flex flex-col gap-1 pb-5">
        <div className="flex-1 flex items-start gap-5">
          <Input
            ref={inputRef}
            value={b2bCodeValue}
            onChange={(e) => {
              b2bCodeChange(e.target.value);
              if (
                e.target.value === codeValidation?.validateB2bCode?.code &&
                codeValidation?.validateB2bCode?.isCodeValid
              ) {
                onCodeValidated(e.target.value);
              } else {
                onCodeValidated(null);
              }
            }}
            placeholder={t({
              id: 'register_v2.form.b2b_code_placeholder',
            })}
          />
          <Button
            className="h-12"
            loading={loading}
            onClick={() => {
              if (codeValidation?.validateB2bCode?.code === b2bCodeValue) {
                (inputRef.current as HTMLInputElement).value = '';
                validateCode({ variables: { code: '' } });
                b2bCodeChange('');
              } else {
                validateCode({
                  variables: {
                    code: (inputRef.current as HTMLInputElement)?.value,
                  },
                });
              }
            }}
            outline
          >
            {b2bCodeValue &&
            codeValidation?.validateB2bCode?.code === b2bCodeValue ? (
              <X className="size-4" />
            ) : (
              <Trans id="register_v2.form.validate_code_btn" />
            )}
          </Button>
        </div>
        {validation_message}
      </div>
      <Banner appearance="default">
        <span>
          <Trans
            id="register_v2.form.existing_account_b2b_message"
            components={[
              <a
                key="0"
                className="text-primary font-medium underline"
                href={`mailto:${CONTACT_EMAIL}`}
              >
                {CONTACT_EMAIL}
              </a>,
            ]}
          />
        </span>
      </Banner>
    </div>
  );
};
